import React, { useContext, useEffect, useMemo, useState } from "react";
import useTranslation, { LanguageContext } from "../util/useTranslate";
import { Button, Container, Row, Image } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ParallaxBackground from "./ParallaxBackground";
import { handleAnswer } from "util/supabase";

const Question = ({ gameSettings, currentPlayer }) => {
  const { language } = useContext(LanguageContext);
  const translate = useTranslation();
  const { gameId } = useParams();
  const [timer, setTimer] = useState(null);

  const sendQuestionAnswer = useMemo(() => (index) => {
    handleAnswer(gameId, currentPlayer.id, index);
  }, [gameId, currentPlayer.id]);

  const playerAnswering = useMemo(
    () =>
      gameSettings.players.find(
        (item) => item.id === gameSettings.question?.playerAnswering
      ),
    [gameSettings]
  );

  const playerGuessed = useMemo(
    () =>
      gameSettings.players.find(
        (item) => item.id === gameSettings.question?.playerGuessed
      ),
    [gameSettings]
  );

  const isCorrectGuess = playerAnswering?.team === playerGuessed?.team

  const guessText = useMemo(() => {
    if(gameSettings.question?.isDouble) {
      return "winCallDouble"
    }
    return gameSettings.question?.guess ? "winCall" : "loseCall" 
  }, [gameSettings])

  const getTeamColor = (team) => {
    return team === 1 ? "red" : "blue";
  };

  const sendTimerExpiredAnswer = useMemo(() => () => {
    handleAnswer(gameId, playerAnswering?.id, -1);
  }, [gameId, playerAnswering?.id]);

  useEffect(() => {
    const calculate = () => {
      const end = new Date(gameSettings.timer).getTime();
      const now = new Date().getTime();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      setTimer(diff);
      return diff;
    };

    // 2. Initial calculation
    const current = calculate();

    // 3. Only set interval if there is time remaining
    if (current > 0) {
      const interval = setInterval(() => {
        const remaining = calculate();
        if (remaining <= 0)  {
            sendTimerExpiredAnswer(); // Automatically answer incorrectly if time runs out
          clearInterval(interval);
        };
      }, 1000);

      return () => clearInterval(interval);
    } else {
      // Time already expired
      sendTimerExpiredAnswer(); // Automatically answer incorrectly if time runs out
    }
  }, [gameSettings.timer, sendTimerExpiredAnswer, currentPlayer, playerAnswering]); // Triggers every time the 'timer' string changes

  return (
    <>
      <Container className="mb-3 mt-3">
        {playerGuessed && (
          <Row className="justify-content-center align-items-center pt-4">
            <h2 style={{ width: "auto" }}>
              <span style={{ color: getTeamColor(playerGuessed.team) }}>
                {playerGuessed.name}
              </span>{" "}
              {isCorrectGuess ? translate("guessed") : translate("notGuessed")}{" "}
              {`„${translate(guessText)}“`}
            </h2>
          </Row>
        )}
        {playerAnswering && (
          <Row className="justify-content-center align-items-center mt-4">
            <h2 style={{ width: "auto" }}>
              {translate("so")}
              {" "}
              <span style={{ color: getTeamColor(playerAnswering.team) }}>
                {playerAnswering.name}
              </span>{" "}
              {translate("isAnsweringQuestion")}
            </h2>
          </Row>
        )}
        <Row className="justify-content-center align-items-center mt-5">
          <div
            style={{
              backgroundImage: `url(/backgrounds/plate.png)`,
              height: "70px",
              width: "70px",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            {timer}
          </div>
        </Row>
        <Row className="justify-content-center align-items-center mt-5">
          <Image
            fluid
            style={{ objectFit: "contain", height: "30vh", minHeight: 400 }}
            src={`/questions/${language}/${
              parseInt(gameSettings.question.id)
            }.jpg`}
          />
        </Row>
        {gameSettings.question.options.map((item, index) => (
          <Row
            key={index}
            className="mt-4 justify-content-center align-items-center"
          >
            <Button
              className="w-50"
              variant="primary"
              disabled={gameSettings.question.playerAnswering !== currentPlayer.id}
              onClick={() => sendQuestionAnswer(index)}
            >
              {translate(item)}
            </Button>
          </Row>
        ))}
      </Container>
      <ParallaxBackground
        src={"/backgrounds/dvopek.png"}
        position={"bottom left"}
        size={"45vh"}
      />
      <ParallaxBackground
        src={"/backgrounds/kapulica.png"}
        position={"bottom right"}
        size={"20vh"}
      />
    </>
  );
};

export default Question;
